/**
 * Pure utilities for applying and clearing change records on a
 * FormspecCondition tree. Shared between bulkEditState and specificationState.
 */

// ============================================================================
// Grid field categorization
// ============================================================================

/**
 * Field names that represent grid chrome rather than condition data.
 * Excluded from any "is this a real cell" check (copy, edit, paste).
 */
export const STRUCTURAL_GRID_FIELDS = new Set([
  "dragHandle",
  "__check__",
  "conditionId",
  "conditionName",
]);

/**
 * True iff `field` references a real condition data column (not a
 * structural column, not an internal `_*` field, not blank). This is the
 * shared base predicate used for clipboard-copy eligibility.
 */
export const isCopyableGridField = (field: string): boolean => {
  if (!field || field.trim() === "") return false;
  if (field.startsWith("_")) return false;
  if (STRUCTURAL_GRID_FIELDS.has(field)) return false;
  return true;
};

/**
 * True iff `field` references a column that can also be inline-edited.
 * Stricter than `isCopyableGridField`: also excludes the synthetic
 * `_part_number` / `_template_name` suffix columns, which are
 * derived/read-only.
 */
export const isEditableGridField = (field: string): boolean => {
  if (!isCopyableGridField(field)) return false;
  const parts = parseColumnKey(field);
  if (parts) {
    if (parts.fieldName === "_part_number") return false;
    if (parts.fieldName === "_template_name") return false;
  }
  return true;
};

// ============================================================================
// Grid column key codec
// ============================================================================
//
// GridView columns are keyed by a single opaque string that encodes:
//   - bomPath:      ">"-separated BOM indices from condition root
//                   ("" for the condition's root item)
//   - typeKind:     "P" for process parameter, "I" for item attribute
//   - typeIdentity: process_type uuid (or name fallback) for "P";
//                   item type name for "I"
//   - instancePath: dot-separated segments locating the process within the
//                   process tree at `bomPath`, one per depth; "0" for items.
//                   The leaf segment is a bare ordinal among same-type
//                   siblings (its type is `typeIdentity`). Ancestor segments
//                   are `<escapedTypeId>:<ordinal>` since the key otherwise
//                   carries no ancestor types. Top-level processes are
//                   therefore just `"N"`. See {@link resolveProcessByInstancePath}.
//   - fieldName:    attribute or parameter name. May contain any character
//                   including "." (e.g. "Resistance: OEE vs. GDE"), so we
//                   use "|" as the segment separator and split on it
//                   directly instead of relying on a dot heuristic.
//
// Encoded form: `${bomPath}|${typeKind}:${typeIdentity}|${instancePath}|${fieldName}`

const COLUMN_KEY_SEP = "|";

export type ColumnKeyParts = {
  bomPath: string;
  typeKind: "P" | "I";
  typeIdentity: string;
  instancePath: string;
  fieldName: string;
};

export const encodeColumnKey = (parts: ColumnKeyParts): string => {
  return [
    parts.bomPath,
    `${parts.typeKind}:${parts.typeIdentity}`,
    parts.instancePath,
    parts.fieldName,
  ].join(COLUMN_KEY_SEP);
};

// Module-level memoization. The encoded form is pure & deterministic, the
// universe of distinct keys is bounded by `allPropertyNames` (typically a
// few hundred), and grid render paths call `parseColumnKey` 2-3 times per
// cell. A simple unbounded Map is the right shape here; if it ever needs
// bounding, swap to a small LRU.
const parseColumnKeyCache = new Map<string, ColumnKeyParts | null>();

export const parseColumnKey = (field: string): ColumnKeyParts | null => {
  const cached = parseColumnKeyCache.get(field);
  if (cached !== undefined) {
    return cached;
  }

  // Field names may contain the separator-adjacent characters but never the
  // separator itself ("|"), so a 4-segment split with the tail re-joined is
  // unambiguous. We split with no limit and re-join the trailing segments
  // defensively in case a future field name ever contains "|".
  const segments = field.split(COLUMN_KEY_SEP);
  if (segments.length < 4) {
    parseColumnKeyCache.set(field, null);
    return null;
  }
  const [bomPath, typeSegment, instancePath, ...fieldNameParts] = segments;
  const fieldName = fieldNameParts.join(COLUMN_KEY_SEP);
  const colonIdx = typeSegment.indexOf(":");
  if (colonIdx < 0) {
    parseColumnKeyCache.set(field, null);
    return null;
  }
  const typeKind = typeSegment.slice(0, colonIdx);
  if (typeKind !== "P" && typeKind !== "I") {
    parseColumnKeyCache.set(field, null);
    return null;
  }
  const result: ColumnKeyParts = {
    bomPath,
    typeKind,
    typeIdentity: typeSegment.slice(colonIdx + 1),
    instancePath,
    fieldName,
  };
  parseColumnKeyCache.set(field, result);
  return result;
};

/**
 * Translate an internal field name into the user-facing label. Handles the
 * synthetic `_part_number` and `_template_name` columns; otherwise returns
 * the raw name.
 */
export const humanizeFieldName = (fieldName: string): string => {
  if (fieldName === "_part_number") return "Part Number";
  if (fieldName === "_template_name") return "Template";
  return fieldName;
};

// ============================================================================
// BOM path navigation
// ============================================================================
//
// Helpers for navigating a FormspecCondition's nested BOM tree by a string
// path. The path format is "{index}>{index}>..." (e.g., "0>1>2"), which
// matches the bom-item URL parameter used across the conditions feature.
// An empty string resolves to the root condition.
//
// These helpers are intentionally dependency-free (no Zustand, no React) so
// they can be reused by any store or component that needs to resolve a path.

/**
 * Parse a bom-item path string into an array of numeric indices.
 * Returns an empty array for an empty path (root).
 * Non-numeric segments are filtered out to guard against malformed input.
 */
export const parseBomPath = (path: string): number[] => {
  if (!path) {
    return [];
  }

  return path
    .split(">")
    .map((segment) => parseInt(segment, 10))
    .filter((segment) => !Number.isNaN(segment));
};

/**
 * Resolve an item by path without mutating or cloning anything.
 * Returns null if the path does not exist in the tree.
 */
export const getItemAtPath = (
  condition: FormspecCondition | null,
  path: string
): ItemWithBOM | null => {
  if (!condition) {
    return null;
  }

  const indices = parseBomPath(path);
  let current: ItemWithBOM = condition;

  for (const index of indices) {
    if (!current.bom || index < 0 || index >= current.bom.length) {
      return null;
    }
    current = current.bom[index];
  }

  return current;
};

/**
 * Navigate the BOM tree along `pathIndices` and apply `applyFn` at the
 * terminal item, returning a new tree with structural sharing above the
 * target. Preserves BOMItem.quantity on intermediate nodes.
 *
 * If any index along the path is out of range, returns the original root
 * item unchanged.
 */
export const navigateBomTree = (
  item: ItemWithBOM,
  pathIndices: number[],
  applyFn: (item: ItemWithBOM) => ItemWithBOM,
  depth: number = 0
): ItemWithBOM => {
  if (depth === pathIndices.length) {
    return applyFn(item);
  }

  const currentIndex = pathIndices[depth];
  if (currentIndex < 0 || currentIndex >= item.bom.length) {
    return item;
  }

  const updatedChild = navigateBomTree(
    item.bom[currentIndex],
    pathIndices,
    applyFn,
    depth + 1
  );

  const updatedBom = [...item.bom];
  updatedBom[currentIndex] = {
    ...updatedChild,
    quantity: item.bom[currentIndex].quantity,
  } as BOMItem;

  return { ...item, bom: updatedBom as BOMItem[] };
};

/**
 * Read-only counterpart of navigateBomTree. Extracts a value at the terminal
 * item without cloning. Returns `defaultValue` if the path is invalid.
 */
export const navigateBomTreeWithResult = <T>(
  item: ItemWithBOM,
  pathIndices: number[],
  applyFn: (item: ItemWithBOM) => T,
  defaultValue: T,
  depth: number = 0
): T => {
  if (depth === pathIndices.length) {
    return applyFn(item);
  }

  const currentIndex = pathIndices[depth];
  if (currentIndex < 0 || currentIndex >= item.bom.length) {
    return defaultValue;
  }

  return navigateBomTreeWithResult(
    item.bom[currentIndex],
    pathIndices,
    applyFn,
    defaultValue,
    depth + 1
  );
};

// ============================================================================
// Process tree navigation
// ============================================================================

/**
 * Resolve the item at `path` along with its parent BOM list and its index
 * within that list. For the root, `parentBom` is null and `leafIndex` is -1.
 *
 * Useful for sibling-aware operations (ambiguity checks, reordering, etc.)
 * that need both the resolved item and its surrounding context in one pass.
 */
export const resolveItemContext = (
  baseline: FormspecCondition | null | undefined,
  path: string
): {
  itemWithBom: ItemWithBOM | null;
  parentBom: BOMItem[] | null;
  leafIndex: number;
} => {
  const indices = parseBomPath(path);
  if (indices.length === 0) {
    return { itemWithBom: baseline ?? null, parentBom: null, leafIndex: -1 };
  }
  const parentPath = indices.slice(0, -1).join(">");
  const parent = getItemAtPath(baseline ?? null, parentPath);
  const leafIndex = indices[indices.length - 1];
  return {
    itemWithBom: parent?.bom?.[leafIndex] ?? null,
    parentBom: parent?.bom ?? null,
    leafIndex,
  };
};

/**
 * Resolve the leaf process at `processPath` along with its sibling list and
 * its index within that list. Symmetric counterpart to
 * {@link resolveItemContext} for process trees.
 *
 * Returns null if `processPath` is empty or any segment is out of range.
 */
export const resolveProcessContext = (
  processes: ProcessWithSteps[] | undefined,
  processPath: number[]
): {
  leafProcess: Process;
  siblings: ProcessWithSteps[];
  leafIndex: number;
} | null => {
  if (processPath.length === 0) {
    return null;
  }
  let siblings: ProcessWithSteps[] = processes ?? [];
  for (let i = 0; i < processPath.length - 1; i += 1) {
    const node = siblings[processPath[i]];
    if (!node) {
      return null;
    }
    siblings = node.steps || [];
  }
  const leafIndex = processPath[processPath.length - 1];
  const leafEntry = siblings[leafIndex];
  if (!leafEntry) {
    return null;
  }
  return { leafProcess: leafEntry.process, siblings, leafIndex };
};

/**
 * Resolve a process entry inside a (possibly nested) process tree by walking
 * `processPath`. Each segment indexes into the `steps` of the previous entry,
 * with the first segment indexing into `processes` itself.
 *
 * Returns `{ list, index }` so callers can either read the entry
 * (`list[index]`) or mutate the list at that location. Returns null if the
 * path is empty or any segment is out of range.
 *
 * NOTE: This does not clone. Callers that need immutability should clone the
 * relevant `steps` arrays along the path themselves before calling, or pass
 * an already-cloned tree.
 */
export const getProcessAtPath = (
  processes: ProcessWithSteps[],
  processPath: number[]
): { list: ProcessWithSteps[]; index: number } | null => {
  if (processPath.length === 0) {
    return null;
  }

  let currentList = processes;

  for (let depth = 0; depth < processPath.length - 1; depth += 1) {
    const index = processPath[depth];
    if (index < 0 || index >= currentList.length) {
      return null;
    }

    const nextEntry = currentList[index];
    currentList = nextEntry.steps || [];
  }

  const finalIndex = processPath[processPath.length - 1];
  if (finalIndex < 0 || finalIndex >= currentList.length) {
    return null;
  }

  return { list: currentList, index: finalIndex };
};

/**
 * Return a copy of `processes` with the entry at `processPath` replaced by
 * `updater(entry)`. Every `steps` array along the path is cloned, so prior
 * state is never mutated. Returns null if the path cannot be resolved.
 */
export const updateProcessAtPath = (
  processes: ProcessWithSteps[],
  processPath: number[],
  updater: (entry: ProcessWithSteps) => ProcessWithSteps
): ProcessWithSteps[] | null => {
  if (processPath.length === 0) {
    return null;
  }
  const [index, ...rest] = processPath;
  if (index < 0 || index >= processes.length) {
    return null;
  }
  const entry = processes[index];
  let nextEntry: ProcessWithSteps;
  if (rest.length === 0) {
    nextEntry = updater(entry);
  } else {
    const nextSteps = updateProcessAtPath(entry.steps || [], rest, updater);
    if (!nextSteps) {
      return null;
    }
    nextEntry = { ...entry, steps: nextSteps };
  }
  const cloned = [...processes];
  cloned[index] = nextEntry;
  return cloned;
};

/**
 * Apply a step add/delete to `parent.step_changes`, mirroring how item-level
 * `process_changes` work:
 * - add: records `{ index, is_added }` (the step itself is appended
 *   separately once fetched, see `appendStep`).
 * - delete of a pending addition: drops both the change and the step, and
 *   shifts later change indices down.
 * - delete of an existing step: records `{ index, is_deleted }`.
 */
export const applyStepListChange = (
  parent: ProcessWithSteps,
  stepIndex: number,
  operation: "add" | "delete"
): ProcessWithSteps => {
  const stepChanges = [...(parent.step_changes || [])];

  if (operation === "add") {
    stepChanges.push({ index: stepIndex, is_added: true, is_deleted: false });
    return { ...parent, step_changes: stepChanges };
  }

  const addedIndex = stepChanges.findIndex(
    (c) => c.index === stepIndex && c.is_added
  );
  if (addedIndex >= 0) {
    stepChanges.splice(addedIndex, 1);
    return {
      ...parent,
      steps: (parent.steps || []).filter((_, i) => i !== stepIndex),
      step_changes: stepChanges.map((c) =>
        c.index > stepIndex ? { ...c, index: c.index - 1 } : c
      ),
    };
  }

  const deletion: ListChange = {
    index: stepIndex,
    is_added: false,
    is_deleted: true,
  };
  const existingIndex = stepChanges.findIndex((c) => c.index === stepIndex);
  if (existingIndex >= 0) {
    stepChanges[existingIndex] = deletion;
  } else {
    stepChanges.push(deletion);
  }
  return { ...parent, step_changes: stepChanges };
};

/** Drop the step list change at `stepIndex` (e.g. undo a deletion). */
export const removeStepListChange = (
  parent: ProcessWithSteps,
  stepIndex: number
): ProcessWithSteps => ({
  ...parent,
  step_changes: (parent.step_changes || []).filter(
    (c) => c.index !== stepIndex
  ),
});

/** Whether a process's own name or type name matches `matches`. */
export const processNameMatches = (
  entry: ProcessWithSteps,
  matches: (text: string) => boolean
): boolean =>
  matches(entry.process.process_type?.name || "") ||
  matches(entry.process.name || "");

/**
 * Whether a process, any of its parameter names, or anything in its step
 * subtree matches `matches`. Used to keep a process visible while searching.
 */
export const processSubtreeMatches = (
  entry: ProcessWithSteps,
  matches: (text: string) => boolean
): boolean =>
  processNameMatches(entry, matches) ||
  Object.keys(entry.process.parameters || {}).some(matches) ||
  (entry.steps || []).some((step) => processSubtreeMatches(step, matches));

/**
 * Indices of `steps` in display order: by Arena `line_number`, with steps
 * that have none (e.g. pending additions) kept in insertion order at the end.
 */
export const sortedStepIndices = (steps: ProcessWithSteps[]): number[] =>
  steps
    .map((step, index) => ({ index, line: step.line_number }))
    .sort((a, b) => {
      const aLine = a.line ?? Number.POSITIVE_INFINITY;
      const bLine = b.line ?? Number.POSITIVE_INFINITY;
      return aLine === bLine ? a.index - b.index : aLine - bLine;
    })
    .map(({ index }) => index);

/**
 * Stable identity for a Process's type. Prefers the type UUID; falls back to
 * the type name. Used as the `typeIdentity` segment of a grid column key
 * for process parameter columns.
 */
export const getProcessTypeId = (process: Process | undefined): string => {
  if (!process) return "Unknown";
  if (process.process_type?.uuid) {
    return process.process_type.uuid;
  }
  return process.process_type?.name || "Unknown";
};

/**
 * Stable identity for an Item's type. Used as the `typeIdentity` segment of
 * a grid column key for item attribute columns. Symmetric counterpart to
 * {@link getProcessTypeId}.
 */
export const getItemTypeName = (
  item: Item | ArenaOnlyItem | undefined
): string => {
  if (!item) return "Unknown";
  return item.item_type_name || "Unknown";
};

/**
 * Names of the attributes the grid shows for an item: those with a value, plus
 * user-defined ones, which FormSpec leaves out of `attributes` until someone
 * fills them in. Based on key presence, so attributes holding falsy values
 * (`false`, `0`, `""`, `null`) count.
 */
export const getItemPropertyNames = (
  item: Item | ArenaOnlyItem | undefined
): string[] => {
  if (!item) return [];
  const userDefined =
    "user_defined_attributes" in item ? item.user_defined_attributes : [];
  return Array.from(
    new Set([...Object.keys(item.attributes || {}), ...(userDefined || [])])
  );
};

/**
 * Names of the parameters the grid shows for a process. Counterpart to
 * {@link getItemPropertyNames}.
 */
export const getProcessPropertyNames = (
  process: Process | undefined
): string[] => {
  if (!process) return [];
  return Array.from(
    new Set([
      ...Object.keys(process.parameters || {}),
      ...(process.user_defined_parameters || []),
    ])
  );
};

// ----------------------------------------------------------------------------
// Process instance paths (the `instancePath` column-key segment)
// ----------------------------------------------------------------------------

/**
 * Order in which same-type sibling ordinals are counted at a process-tree
 * level. Top-level processes use array order; steps use their Arena line
 * order (matching ProcessesSection), so ordinals don't depend on the order
 * FormSpec happens to return steps in.
 */
export const processSiblingOrder = (
  entries: ProcessWithSteps[],
  depth: number
): number[] =>
  depth === 0 ? entries.map((_, index) => index) : sortedStepIndices(entries);

// Type ids can be names (fallback) containing "." / ":" / "|", which are
// instance-path or column-key separators, so escape them.
const escapeInstanceTypeId = (typeId: string): string =>
  encodeURIComponent(typeId).replace(/\./g, "%2E");

/**
 * Ancestor segment of an instance path: `<escapedTypeId>:<ordinal>`.
 * The leaf segment is the bare ordinal (its type is the key's typeIdentity).
 */
export const ancestorInstanceSegment = (
  typeId: string,
  ordinal: number
): string => `${escapeInstanceTypeId(typeId)}:${ordinal}`;

/** Append `segment` to an instance-path `prefix` ("" for the root level). */
export const joinInstancePath = (prefix: string, segment: string): string =>
  prefix ? `${prefix}.${segment}` : segment;

export type InstancePathSegment = {
  /** Ancestor type id; null for the leaf (uses the key's typeIdentity). */
  typeId: string | null;
  ordinal: number;
};

/**
 * Parse an instance path into segments. Only the last segment may omit its
 * type. Returns null if malformed.
 */
export const parseProcessInstancePath = (
  instancePath: string
): InstancePathSegment[] | null => {
  const rawSegments = instancePath.split(".");
  const segments: InstancePathSegment[] = [];
  for (let i = 0; i < rawSegments.length; i += 1) {
    const raw = rawSegments[i];
    const colonIdx = raw.lastIndexOf(":");
    const isLeaf = i === rawSegments.length - 1;
    if (colonIdx < 0 && !isLeaf) {
      return null;
    }
    const ordinalText = colonIdx < 0 ? raw : raw.slice(colonIdx + 1);
    if (!/^\d+$/.test(ordinalText)) {
      return null;
    }
    let typeId: string | null = null;
    if (colonIdx >= 0) {
      try {
        typeId = decodeURIComponent(raw.slice(0, colonIdx));
      } catch {
        return null;
      }
    }
    segments.push({ typeId, ordinal: parseInt(ordinalText, 10) });
  }
  return segments;
};

/** Ordinals of an instance path, for sorting `#N` disambiguators. */
export const instancePathOrdinals = (instancePath: string): number[] =>
  (parseProcessInstancePath(instancePath) || []).map(({ ordinal }) => ordinal);

/**
 * Every process in a process tree with its column-key `typeIdentity` and
 * `instancePath`. Inverse of {@link resolveProcessByInstancePath}.
 */
export const enumerateProcessInstances = (
  processes: ProcessWithSteps[] | undefined,
  ancestorPrefix = "",
  depth = 0
): {
  entry: ProcessWithSteps;
  typeIdentity: string;
  instancePath: string;
}[] => {
  if (!processes) return [];
  const result: {
    entry: ProcessWithSteps;
    typeIdentity: string;
    instancePath: string;
  }[] = [];
  const sameTypeOrdinals = new Map<string, number>();

  for (const index of processSiblingOrder(processes, depth)) {
    const entry = processes[index];
    const typeIdentity = getProcessTypeId(entry.process);
    const ordinal = sameTypeOrdinals.get(typeIdentity) ?? 0;
    sameTypeOrdinals.set(typeIdentity, ordinal + 1);

    result.push({
      entry,
      typeIdentity,
      instancePath: joinInstancePath(ancestorPrefix, String(ordinal)),
    });
    result.push(
      ...enumerateProcessInstances(
        entry.steps,
        joinInstancePath(
          ancestorPrefix,
          ancestorInstanceSegment(typeIdentity, ordinal)
        ),
        depth + 1
      )
    );
  }
  return result;
};

/**
 * Walk a process subtree by an instance path (see the column key codec).
 * Each segment selects the Nth process of that segment's type among its
 * siblings — counted in {@link processSiblingOrder} — then recurses into
 * that process's `steps`. Ancestor segments carry their own type; the leaf
 * uses `typeIdentity`.
 *
 * Example: `instancePath = "<ParentType>:0.1"` with
 * `typeIdentity = "<StepType>"` means "the first ParentType process at the
 * root, then its second StepType step".
 *
 * Returns the matched ProcessWithSteps along with its concrete numeric path
 * (suitable for {@link applyPropertyEdit}'s parameter target). Returns null
 * if the path is malformed or any segment cannot be resolved.
 */
export const resolveProcessByInstancePath = (
  processes: ProcessWithSteps[] | undefined,
  typeIdentity: string,
  instancePath: string
): { entry: ProcessWithSteps; processPath: number[] } | null => {
  if (!processes) {
    return null;
  }
  const segments = parseProcessInstancePath(instancePath);
  if (!segments) {
    return null;
  }

  let currentList: ProcessWithSteps[] = processes;
  const processPath: number[] = [];

  for (let depth = 0; depth < segments.length; depth += 1) {
    const isLeaf = depth === segments.length - 1;
    const { ordinal } = segments[depth];
    const segmentType = isLeaf ? typeIdentity : segments[depth].typeId;
    let matchCount = 0;
    let matchedIndex = -1;

    for (const i of processSiblingOrder(currentList, depth)) {
      if (getProcessTypeId(currentList[i].process) === segmentType) {
        if (matchCount === ordinal) {
          matchedIndex = i;
          break;
        }
        matchCount += 1;
      }
    }

    if (matchedIndex < 0) {
      return null;
    }

    processPath.push(matchedIndex);
    if (isLeaf) {
      return { entry: currentList[matchedIndex], processPath };
    }
    currentList = currentList[matchedIndex].steps || [];
  }

  return null;
};

const isObjectLike = (value: unknown): value is Record<string, unknown> => {
  return typeof value === "object" && value !== null;
};

/**
 * Coerce a numeric-looking string to a number for comparison purposes.
 * Returns the value unchanged for non-numeric strings, booleans, null, etc.
 * Mirrors the backend's `_coerce_numeric_string` so that a value entered as
 * the string "180" is treated as equal to the saved numeric 180
 */
const numericStringToNumber = (
  value: ValueType | null | undefined
): ValueType | null | undefined => {
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed !== "") {
      const parsed = Number(trimmed);
      if (!Number.isNaN(parsed) && Number.isFinite(parsed)) {
        return parsed;
      }
    }
  }
  return value;
};

/**
 * Structural equality for ValueType (plus nullish). Uses JSON.stringify for
 * object-like values so that RangeValue / PartNumber shapes compare by value
 * rather than reference. Numeric strings are compared numerically (so "180"
 * equals 180) to match how FormSpec stores Arena-round-tripped values.
 */
export const areValuesEqual = (
  a: ValueType | null | undefined,
  b: ValueType | null | undefined
): boolean => {
  if (a === b) {
    return true;
  }

  if (a == null && b == null) {
    return true;
  }

  if (isObjectLike(a) && isObjectLike(b)) {
    return JSON.stringify(a) === JSON.stringify(b);
  }

  // Numeric-aware comparison: treat "180" and 180 as equal. Booleans are not
  // coerced (numericStringToNumber leaves them untouched), so true !== 1.
  const na = numericStringToNumber(a);
  const nb = numericStringToNumber(b);
  if (typeof na === "number" && typeof nb === "number") {
    return na === nb;
  }

  return false;
};

/**
 * Deep-clone a FormspecCondition via JSON round-trip. Safe because the
 * condition tree contains only plain serialisable data (no Date, Map, etc).
 */
export const cloneCondition = (
  condition: FormspecCondition
): FormspecCondition => {
  return JSON.parse(JSON.stringify(condition)) as FormspecCondition;
};

/**
 * Insert, update, or remove a PropertyChange in a changes array based on
 * whether the new value differs from the previous value. Mutates `changes`
 * in place.
 *
 * If `operation` is provided (specificationState semantics), `is_added` and
 * `is_deleted` are "sticky": once set on an existing change, they persist
 * through subsequent edits. If omitted (bulkEditState semantics), the flags
 * are derived from null-ness of the old/new values.
 */
export const upsertPropertyChange = (
  changes: PropertyChange[],
  propertyName: string,
  oldValue: ValueType | null | undefined,
  newValue: ValueType | null | undefined,
  operation?: "add" | "update" | "delete"
): void => {
  const existingIndex = changes.findIndex(
    (entry) => entry.name === propertyName
  );

  if (areValuesEqual(oldValue, newValue)) {
    if (existingIndex >= 0) {
      changes.splice(existingIndex, 1);
    }
    return;
  }

  const existing = existingIndex >= 0 ? changes[existingIndex] : undefined;

  const isAdded =
    operation !== undefined
      ? existing?.is_added || operation === "add"
      : oldValue == null;

  const isDeleted =
    operation !== undefined
      ? existing?.is_deleted || operation === "delete"
      : newValue == null;

  const change: PropertyChange = {
    name: propertyName,
    old_value: oldValue ?? undefined,
    new_value: newValue ?? undefined,
    is_added: Boolean(isAdded),
    is_deleted: Boolean(isDeleted),
  };

  if (existingIndex >= 0) {
    // Preserve the original old_value from when the edit session began.
    // Without this, rapid keystrokes overwrite old_value with the value
    // from one keystroke prior (e.g. typing "181" leaves old_value as "18"
    // instead of the actual saved baseline).
    changes[existingIndex] = {
      ...change,
      old_value: changes[existingIndex].old_value,
    };
  } else {
    changes.push(change);
  }
};

/**
 * Whether a process or any of its nested steps has pending changes
 * (parameter edits in `process_changes` or step additions/deletions in
 * `step_changes`).
 */
export const processTreeHasChanges = (
  processWithSteps: ProcessWithSteps
): boolean =>
  (processWithSteps.process_changes?.length ?? 0) > 0 ||
  (processWithSteps.step_changes?.length ?? 0) > 0 ||
  (processWithSteps.steps || []).some(processTreeHasChanges);

/**
 * Return a new ProcessWithSteps with all change arrays cleared, recursively
 * applied through nested steps.
 */
export const clearProcessTreeChanges = (
  processWithSteps: ProcessWithSteps
): ProcessWithSteps => {
  return {
    ...processWithSteps,
    process_changes: [],
    step_changes: [],
    steps: (processWithSteps.steps || []).map(clearProcessTreeChanges),
  };
};

/**
 * Normalize a `formspec/processes/:uuid` response (always a ProcessWithSteps;
 * `steps` is only populated with `include_steps=true`) by initializing empty
 * change arrays at every level.
 */
export const toProcessWithSteps = (data: ProcessWithSteps): ProcessWithSteps =>
  clearProcessTreeChanges(data);

/**
 * Return a new ItemWithBOM with every change array cleared, recursively
 * applied through the full BOM and process trees. This is the canonical
 * "reset all pending edits" helper.
 */
export const clearItemTreeChanges = <T extends ItemWithBOM>(item: T): T => {
  return {
    ...item,
    item_changes: [],
    bom_changes: [],
    process_changes: [],
    item_replaced: false,
    processes: (item.processes || []).map(clearProcessTreeChanges),
    bom: (item.bom || []).map(clearItemTreeChanges) as BOMItem[],
  } as T;
};

// ============================================================================
// Unified property edit
// ============================================================================

/**
 * Target for a single property edit. A subset of the fuller ChangeTarget
 * shape in specificationState — covers only attribute/parameter writes,
 * which are the operations that both stores share.
 */
export type PropertyEditTarget =
  | { type: "attribute"; path: string; propertyName: string }
  | {
      type: "parameter";
      path: string;
      processPath: number[];
      propertyName: string;
    };

/**
 * Apply a property edit to a target item inside a condition tree.
 *
 * Writes `currentValue` to either `item.attributes[propertyName]`
 * (attribute edits) or `process.parameters[propertyName]` (parameter edits),
 * and upserts the matching PropertyChange into `item_changes` or the target
 * process's `process_changes` via {@link upsertPropertyChange}.
 *
 * Mutation contract: mutates `targetItem` and its descendants in place. The
 * caller is responsible for ensuring `targetItem` is either a deep clone
 * they own, or the leaf of a structurally-shared walk where mutating the
 * leaf's fields is safe (e.g., the item returned from the `applyFn` passed
 * to `navigateBomTree`). For the parameter branch, the `processes` array and
 * the `steps` arrays along `processPath` are defensively cloned so callers
 * that pass a shallow-cloned leaf do not corrupt shared state above.
 *
 * The optional `operation` parameter controls is_added/is_deleted flag
 * semantics on the resulting PropertyChange: see
 * {@link upsertPropertyChange} for details.
 *
 * Returns true if the edit was applied, false if the target could not be
 * resolved (e.g., processPath empty or out of range, target item has no
 * processes).
 */
export const applyPropertyEdit = (
  targetItem: ItemWithBOM,
  target: PropertyEditTarget,
  previousValue: ValueType | null | undefined,
  currentValue: ValueType | null | undefined,
  operation?: "add" | "update" | "delete"
): boolean => {
  if (target.type === "attribute") {
    targetItem.item = {
      ...targetItem.item,
      attributes: {
        ...(targetItem.item.attributes || {}),
        [target.propertyName]: currentValue ?? "",
      },
    };

    const itemChanges = [...(targetItem.item_changes || [])];
    upsertPropertyChange(
      itemChanges,
      target.propertyName,
      previousValue,
      currentValue,
      operation
    );
    targetItem.item_changes = itemChanges;
    return true;
  }

  // Parameter edit: walk processPath, cloning each level so upstream state
  // is not aliased to our mutation.
  if (!targetItem.processes || target.processPath.length === 0) {
    return false;
  }

  const clonedProcesses = [...targetItem.processes];
  let currentList: ProcessWithSteps[] = clonedProcesses;

  for (let depth = 0; depth < target.processPath.length - 1; depth += 1) {
    const idx = target.processPath[depth];
    if (idx < 0 || idx >= currentList.length) {
      return false;
    }
    const clonedEntry: ProcessWithSteps = {
      ...currentList[idx],
      steps: [...(currentList[idx].steps || [])],
    };
    currentList[idx] = clonedEntry;
    currentList = clonedEntry.steps;
  }

  const leafIndex = target.processPath[target.processPath.length - 1];
  if (leafIndex < 0 || leafIndex >= currentList.length) {
    return false;
  }

  const leafProcess = { ...currentList[leafIndex] };
  leafProcess.process = {
    ...leafProcess.process,
    parameters: {
      ...(leafProcess.process.parameters || {}),
      [target.propertyName]: currentValue ?? "",
    },
  };

  const processChanges = [...(leafProcess.process_changes || [])];
  upsertPropertyChange(
    processChanges,
    target.propertyName,
    previousValue,
    currentValue,
    operation
  );
  leafProcess.process_changes = processChanges;

  currentList[leafIndex] = leafProcess;
  targetItem.processes = clonedProcesses;
  return true;
};

// ============================================================================
// Constraint helpers
// ============================================================================

/**
 * True iff any constraint in `constraints` represents a "locked" field — i.e.
 * an attribute or parameter whose value cannot be edited from the UI.
 * Centralizes the `attribute_lock` / `parameter_lock` string literals so every
 * editability gate (TrackingInputs, GridView's per-cell `_locked_*` flag, etc.)
 * agrees on the rule.
 *
 * Callers are expected to resolve the field's constraint list themselves via
 * `getAttributeConstraints` / `getParameterConstraints` (see
 * `validationState.ts`). This keeps `utils/conditions.ts` free of any
 * Zustand / React-Query coupling that would creep in if we did the lookup
 * here.
 */
export const isFieldLocked = (
  constraints: TemplateConstraintModel[]
): boolean =>
  constraints.some(
    (c) =>
      c.constraint_type === "attribute_lock" ||
      c.constraint_type === "parameter_lock"
  );

/**
 * True iff any constraint in `constraints` marks an attribute or parameter as
 * required at the item/process (template) level. Callers should OR this with
 * the property's own `is_required` flag from the parent item/process type so a
 * template constraint behaves exactly like a type-level requirement.
 */
export const isFieldRequiredByConstraint = (
  constraints: TemplateConstraintModel[]
): boolean =>
  constraints.some(
    (c) =>
      c.constraint_type === "required_attribute" ||
      c.constraint_type === "required_parameter"
  );

/**
 * Return the first constraint in `constraints` of the given type, narrowed to
 * the matching `TemplateConstraintModel` variant, or null if none exists.
 */
export const findConstraintOfType = <
  T extends TemplateConstraintModel["constraint_type"]
>(
  constraints: TemplateConstraintModel[],
  type: T
): Extract<TemplateConstraintModel, { constraint_type: T }> | null =>
  (constraints.find((c) => c.constraint_type === type) as
    | Extract<TemplateConstraintModel, { constraint_type: T }>
    | undefined) ?? null;

/**
 * Build a router link target that opens the BOM edit view focused on the
 * given condition, preserving any other existing search params. Used by
 * StatusView and GridView to navigate from a condition row to its edit view.
 */
export const createConditionLink = (
  pathname: string,
  searchParams: URLSearchParams,
  conditionId: number
): { pathname: string; search: string } => {
  const params = new URLSearchParams(searchParams.toString());
  params.set("view", "bom");
  params.set("condition", String(conditionId));
  const nextSearch = params.toString();

  return {
    pathname,
    search: nextSearch ? `?${nextSearch}` : "",
  };
};
